import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FiShield,
  FiMail,
  FiPhone,
  FiCheckCircle,
  FiLock,
  FiArrowRight,
  FiRefreshCw,
  FiUser,
  FiMapPin,
  FiActivity,
  FiCheck,
} from 'react-icons/fi';
import { END_POINT } from '../../Redux/AdminReducer/action';
import CustomInput from '../../Components/CommonComponents/CustomInput';
import CustomSelect from '../../Components/CommonComponents/CustomSelect';
import CustomButton from '../../Components/CommonComponents/CustomButton';
import CustomSpinner from '../../Components/CommonComponents/CustomSpinner';
import countiresData from '../../Files/countries.json';
import statesData from '../../Files/states.json';
import citiesData from '../../Files/cities.json';
import usePincodeLookup from '../../hooks/usePincodeLookup';
import usePublicDropdownOptions, { sameOption, withCurrentValue } from '../../hooks/usePublicDropdownOptions';

// ---------------------------------------------------------------------------
// TEMPORARY TESTING SWITCH — set back to false when you are done.
// While true, the page skips "send code / enter code" and drops you straight on
// the edit form. The matching switch in
// backend/Controllers/Public/PublicPatientUpdateController.js must also be true,
// or saving will still be refused.
// ---------------------------------------------------------------------------
const SKIP_OTP_FOR_TESTING = true;

export default function PublicPatientUpdate() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  // Page phases: 'loading' | 'request_otp' | 'enter_otp' | 'form' | 'success'
  const [phase, setPhase] = useState('loading');
  const [info, setInfo] = useState(null);
  // Clinic name + logo from the clinic profile of this patient's tenant
  const [branding, setBranding] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);

  // OTP states
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputsRef = useRef([]);

  // Verified session & form states
  const [verifiedToken, setVerifiedToken] = useState('');
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Location helpers
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);

  const countries = countiresData?.countries || [];
  const states = statesData?.states || [];
  const cities = citiesData?.cities || [];

  // Option lists from the clinic's DropDowns configuration (built-in lists on
  // failure). The patient's saved value always stays selectable.
  const dropdowns = usePublicDropdownOptions();
  const optionsFor = (key, field) => withCurrentValue(dropdowns[key], formData[field]);
  const isMarried = sameOption(formData.maritalStatus, 'Married');

  // Common request headers for tenant resolution
  const getHeaders = (extra = {}) => ({
    'Content-Type': 'application/json',
    'X-Frontend-Host': window.location.host,
    ...extra,
  });

  // 1. Fetch public patient info on mount
  useEffect(() => {
    if (!patientId) return;

    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${END_POINT}/public/patient/update/${patientId}/info`, {
          headers: getHeaders(),
        });
        if (res.data?.success) {
          setInfo(res.data.data);
          if (SKIP_OTP_FOR_TESTING) {
            // Any code is accepted while the backend switch is on.
            await runVerify('000000');
          } else {
            setPhase('request_otp');
          }
        } else {
          toast.error(res.data?.error || 'Could not load patient information');
        }
      } catch (err) {
        console.error('fetchInfo error:', err);
        toast.error(err?.response?.data?.error || 'Invalid or expired patient link');
      }
    };

    fetchInfo();
  }, [patientId]);

  // Clinic branding for the header (public, no login). Initials are shown
  // until it loads, when no logo is set, or if the image fails.
  useEffect(() => {
    axios
      .get(`${END_POINT}/public/clinic-branding`, { headers: getHeaders() })
      .then((res) => {
        if (res.data?.success) setBranding(res.data.data);
      })
      .catch(() => {});
  }, []);

  const clinicName = branding?.name || info?.clinicName || 'Clinic Management';
  const clinicInitials =
    clinicName
      .split(/\s+/)
      .filter((w) => /^[a-z0-9]/i.test(w))
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('') || 'C';
  const showLogo = Boolean(branding?.logoUrl) && !logoFailed;

  // 2. Cooldown timer countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // 3. Country / State / City cascading
  useEffect(() => {
    if (selectedCountry) {
      const filtered = states
        .filter((st) => Number(st.country_id) === Number(selectedCountry))
        .map((s) => s.name);
      setFilteredStates(filtered);
    } else {
      setFilteredStates([]);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedState) {
      const filtered = cities
        .filter((c) => Number(c.state_id) === Number(selectedState))
        .map((c) => c.name);
      setFilteredCities(filtered);
    } else {
      setFilteredCities([]);
    }
  }, [selectedState]);

  // Handle Send OTP
  const handleSendOtp = async () => {
    if (!info?.hasEmail) {
      toast.error('No email address is linked to this record. Please contact clinic staff.');
      return;
    }
    setIsSendingOtp(true);
    try {
      const res = await axios.post(
        `${END_POINT}/public/patient/update/${patientId}/send-otp`,
        {},
        { headers: getHeaders() }
      );
      if (res.data?.success) {
        toast.success(res.data.message || 'Verification code sent to your email!');
        setPhase('enter_otp');
        setResendCooldown(60);
        // Focus first OTP input
        setTimeout(() => {
          otpInputsRef.current[0]?.focus();
        }, 150);
      }
    } catch (err) {
      console.error('handleSendOtp err:', err);
      toast.error(err?.response?.data?.error || 'Failed to send verification code. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle single digit input in OTP boxes
  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    // Handle paste of full 6 digit string
    if (value.length > 1) {
      const pastedDigits = value.replace(/\D/g, '').slice(0, 6).split('');
      pastedDigits.forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtp(newOtp);
      const nextFocus = Math.min(pastedDigits.length, 5);
      otpInputsRef.current[nextFocus]?.focus();
      return;
    }

    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-advance
    if (value && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    const otpCode = otp.join('').trim();
    if (otpCode.length !== 6) {
      toast.error('Please enter the complete 6-digit verification code');
      return;
    }
    await runVerify(otpCode);
  };

  // The request itself, split out so the testing bypass can call it with a dummy
  // code without going through the on-screen boxes.
  const runVerify = async (otpCode) => {
    setIsVerifyingOtp(true);
    try {
      const res = await axios.post(
        `${END_POINT}/public/patient/update/${patientId}/verify-otp`,
        { otp: otpCode },
        { headers: getHeaders() }
      );

      if (res.data?.success) {
        toast.success('Identity verified successfully!');
        setVerifiedToken(res.data.verifiedToken);
        const pData = res.data.patientData || {};
        setFormData(pData);

        // Prepopulate country & state IDs for cascading dropdowns
        const foundCountry = countries.find((c) => c.name === pData.country);
        if (foundCountry) {
          setSelectedCountry(foundCountry.id);
          const foundState = states.find(
            (s) => s.name === pData.state && Number(s.country_id) === Number(foundCountry.id)
          );
          if (foundState) {
            setSelectedState(foundState.id);
          }
        }

        setPhase('form');
      }
    } catch (err) {
      console.error('handleVerifyOtp err:', err);
      toast.error(err?.response?.data?.error || 'Verification failed. Please check the code.');
      // If the bypass was refused (backend switch still false, or NODE_ENV is
      // production) fall back to the normal flow instead of a stuck spinner.
      setPhase((current) => (current === 'loading' ? 'request_otp' : current));
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Handle Form Inputs
  // Auto-fill country / state / city / area from a 6-digit pincode,
  // keeping the cascading dropdowns in sync with the filled values.
  const handlePincodeResult = useCallback((details) => {
    const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
    const country = countries.find((c) => same(c.name, details.country));
    const state = country && states.find((st) => same(st.name, details.state) && Number(st.country_id) === Number(country.id));
    const stateCities = state ? cities.filter((c) => Number(c.state_id) === Number(state.id)) : [];
    const city = stateCities.find((c) => same(c.name, details.city)) || stateCities.find((c) => same(c.name, details.district));
    if (country) setSelectedCountry(country.id);
    if (state) setSelectedState(state.id);
    setFormData((prev) => ({
      ...prev,
      country: country?.name || details.country || prev.country,
      state: state?.name || details.state || prev.state,
      city: city?.name || details.city || prev.city,
      area: details.area || prev.area,
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { onPincodeChange, loading: pincodeLoading, error: pincodeError } = usePincodeLookup(handlePincodeResult);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formError[name]) {
      setFormError((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Handle Save / Submit
  const handleSaveForm = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.firstName?.trim()) {
      errors.firstName = 'First Name is required';
    }
    if (!formData.surname?.trim()) {
      errors.surname = 'Last Name is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormError(errors);
      toast.error('Please fix required fields marked in red');
      return;
    }

    setIsSaving(true);
    try {
      const res = await axios.put(
        `${END_POINT}/public/patient/update/${patientId}`,
        { formData },
        {
          headers: getHeaders({
            'x-patient-update-token': verifiedToken,
          }),
        }
      );

      if (res.data?.success) {
        toast.success(res.data.msg || 'Your details were updated successfully!');
        setPhase('success');
      }
    } catch (err) {
      console.error('handleSaveForm err:', err);
      toast.error(err?.response?.data?.error || 'Failed to update details. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col justify-between py-6 px-4 sm:px-6 lg:px-8">
      {/* Top Branding Bar */}
      <div className="max-w-4xl mx-auto w-full mb-4">
        <div className="flex items-center justify-between bg-white px-5 py-3.5 rounded-xl border border-slate-200/80 shadow-xs">
          <div className="flex items-center gap-3">
            {showLogo ? (
              <img
                src={branding.logoUrl}
                alt={clinicName}
                className="h-10 w-auto max-w-[140px] object-contain rounded-lg"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center font-bold text-base border border-sky-100">
                {clinicInitials}
              </div>
            )}
            <div>
              <h1 className="text-base font-semibold text-slate-800 leading-tight">
                {clinicName}
              </h1>
              <p className="text-xs text-slate-500">Secure Patient Information Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
            <FiShield className="w-3.5 h-3.5" />
            <span className="font-medium">256-bit Secure</span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col justify-center">
        {phase === 'loading' && (
          <div className="bg-white p-12 rounded-2xl border border-slate-200 shadow-sm text-center">
            <CustomSpinner />
            <p className="mt-4 text-sm text-slate-600 font-medium">Verifying access link...</p>
          </div>
        )}

        {/* Phase 1: Request OTP Screen */}
        {phase === 'request_otp' && (
          <div className="max-w-md mx-auto w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <div className="w-12 h-12 bg-sky-50 text-sky-600 rounded-xl flex items-center justify-center mx-auto mb-4 border border-sky-100">
              <FiLock className="w-6 h-6" />
            </div>

            <h2 className="text-xl font-bold text-slate-900 text-center">
              Verify Your Identity
            </h2>
            <p className="mt-1.5 text-xs sm:text-sm text-slate-600 text-center leading-relaxed">
              Hello <strong className="text-slate-800">{info?.fullName}</strong>. To protect your private health data, we require a quick verification code before you can view and update your details.
            </p>

            <div className="mt-6 bg-slate-50 border border-slate-200/80 rounded-xl p-4 space-y-2.5">
              <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-700">
                <FiMail className="w-4 h-4 text-sky-600 shrink-0" />
                <span>
                  Registered Email:{' '}
                  <strong className="text-slate-900 font-mono">
                    {info?.maskedEmail || 'Not available'}
                  </strong>
                </span>
              </div>
              {info?.maskedMobile && (
                <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-700">
                  <FiPhone className="w-4 h-4 text-slate-500 shrink-0" />
                  <span>
                    Phone on file:{' '}
                    <strong className="text-slate-900 font-mono">
                      {info?.maskedMobile}
                    </strong>
                  </span>
                </div>
              )}
            </div>

            {!info?.hasEmail ? (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs sm:text-sm leading-relaxed">
                <strong>Attention:</strong> No email address is currently registered for your account. Please contact the clinic receptionist or doctor so they can add your email to proceed.
              </div>
            ) : (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={isSendingOtp}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-medium text-sm transition shadow-sm disabled:opacity-60 cursor-pointer"
                >
                  {isSendingOtp ? (
                    <>
                      <FiRefreshCw className="w-4 h-4 animate-spin" />
                      Sending Verification Code...
                    </>
                  ) : (
                    <>
                      Send Verification Code
                      <FiArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Phase 2: Enter OTP Screen */}
        {phase === 'enter_otp' && (
          <div className="max-w-md mx-auto w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <div className="w-12 h-12 bg-sky-50 text-sky-600 rounded-xl flex items-center justify-center mx-auto mb-4 border border-sky-100">
              <FiShield className="w-6 h-6" />
            </div>

            <h2 className="text-xl font-bold text-slate-900 text-center">
              Enter Verification Code
            </h2>
            <p className="mt-1.5 text-xs sm:text-sm text-slate-600 text-center leading-relaxed">
              We sent a 6-digit code to{' '}
              <strong className="text-slate-900 font-mono">{info?.maskedEmail}</strong>. Please enter it below:
            </p>

            {/* 6 Digit Inputs */}
            <div className="mt-6 flex justify-center gap-2 sm:gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputsRef.current[index] = el)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="w-11 h-13 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold font-mono text-slate-900 border-2 border-slate-300 rounded-xl focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 transition bg-slate-50 focus:bg-white"
                />
              ))}
            </div>

            {/* Verify Button */}
            <div className="mt-6">
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={isVerifyingOtp || otp.join('').length !== 6}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-medium text-sm transition shadow-sm disabled:opacity-50 cursor-pointer"
              >
                {isVerifyingOtp ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify & Continue
                    <FiCheck className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            {/* Resend Cooldown */}
            <div className="mt-5 text-center">
              {resendCooldown > 0 ? (
                <p className="text-xs text-slate-500">
                  Resend code in{' '}
                  <span className="font-semibold text-slate-700 font-mono">
                    {resendCooldown}s
                  </span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={isSendingOtp}
                  className="text-xs font-semibold text-sky-600 hover:text-sky-800 transition cursor-pointer hover:underline"
                >
                  Didn't receive the email? Click to resend
                </button>
              )}
            </div>
          </div>
        )}

        {/* Phase 3: Patient Information Form */}
        {phase === 'form' && (
          <form
            onSubmit={handleSaveForm}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-8"
          >
            {SKIP_OTP_FOR_TESTING && (
              <div className="rounded-xl border-2 border-dashed border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                Testing mode: OTP verification is switched off, so anyone with this link can edit this patient. Set
                SKIP_OTP_FOR_TESTING back to false before using this for real.
              </div>
            )}

            {/* Header / Intro */}
            <div className="border-b border-slate-200 pb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Update Your Details
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                  Please review and ensure all information is up to date for your medical records.
                </p>
              </div>
              <span className="self-start sm:self-auto text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200 flex items-center gap-1.5">
                <FiCheckCircle className="w-3.5 h-3.5" />
                Verified Patient
              </span>
            </div>

            {/* Section 1: Personal Details */}
            <div>
              <h3 className="flex items-center gap-2 text-sm sm:text-base font-semibold text-slate-800 border-l-4 border-sky-500 pl-3 mb-4">
                <FiUser className="w-4 h-4 text-sky-600" />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <CustomInput
                  label="First Name *"
                  name="firstName"
                  value={formData.firstName || ''}
                  onChange={handleInputChange}
                  error={formError.firstName}
                  placeholder="Enter first name"
                />
                <CustomInput
                  label="Middle Name"
                  name="middleName"
                  value={formData.middleName || ''}
                  onChange={handleInputChange}
                  placeholder="Enter middle name"
                />
                <CustomInput
                  label="Last Name *"
                  name="surname"
                  value={formData.surname || ''}
                  onChange={handleInputChange}
                  error={formError.surname}
                  placeholder="Enter last name"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <CustomSelect
                  label="Gender"
                  name="gender"
                  value={formData.gender || ''}
                  onChange={handleInputChange}
                  options={optionsFor('gender', 'gender')}
                  placeholder="Select Gender"
                />
                <CustomInput
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth || ''}
                  onChange={handleInputChange}
                />
                <CustomSelect
                  label="Blood Group"
                  name="bloodGroup"
                  value={formData.bloodGroup || ''}
                  onChange={handleInputChange}
                  options={optionsFor('blood_group', 'bloodGroup')}
                  placeholder="Select Blood Group"
                />
                <CustomSelect
                  label="Marital Status"
                  name="maritalStatus"
                  value={formData.maritalStatus || ''}
                  onChange={handleInputChange}
                  options={optionsFor('marital_status', 'maritalStatus')}
                  placeholder="Select Status"
                />
              </div>

              {isMarried && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <CustomInput
                    label="Anniversary Date"
                    name="anniversary"
                    type="date"
                    value={formData.anniversary || ''}
                    onChange={handleInputChange}
                  />
                  <CustomSelect
                    label="Mother Tongue"
                    name="motherTongue"
                    value={formData.motherTongue || ''}
                    onChange={handleInputChange}
                    options={optionsFor('mother_tongue', 'motherTongue')}
                    placeholder="Select Mother Tongue"
                  />
                  <CustomSelect
                    label="Religion"
                    name="religion"
                    value={formData.religion || ''}
                    onChange={handleInputChange}
                    options={optionsFor('religion', 'religion')}
                    placeholder="Select Religion"
                  />
                </div>
              )}

              {!isMarried && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <CustomSelect
                    label="Mother Tongue"
                    name="motherTongue"
                    value={formData.motherTongue || ''}
                    onChange={handleInputChange}
                    options={optionsFor('mother_tongue', 'motherTongue')}
                    placeholder="Select Mother Tongue"
                  />
                  <CustomSelect
                    label="Religion"
                    name="religion"
                    value={formData.religion || ''}
                    onChange={handleInputChange}
                    options={optionsFor('religion', 'religion')}
                    placeholder="Select Religion"
                  />
                </div>
              )}
            </div>

            {/* Section 2: Contact & Address Details */}
            <div>
              <h3 className="flex items-center gap-2 text-sm sm:text-base font-semibold text-slate-800 border-l-4 border-sky-500 pl-3 mb-4">
                <FiMapPin className="w-4 h-4 text-sky-600" />
                Contact & Address
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <CustomInput
                    label="Phone Number"
                    value={formData.mobile || ''}
                    disabled={true}
                    placeholder="Phone number"
                  />
                  <span className="text-[11px] text-slate-400 mt-0.5 block">
                    Contact number is managed by clinic staff
                  </span>
                </div>
                <div>
                  <CustomInput
                    label="Email Address"
                    value={formData.email || ''}
                    disabled={true}
                    placeholder="Email address"
                  />
                  <span className="text-[11px] text-slate-400 mt-0.5 block">
                    Verified registered email
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <CustomSelect
                  label="Country"
                  name="country"
                  value={formData.country || ''}
                  options={countries.map((c) => c.name)}
                  onChange={(e) => {
                    handleInputChange(e);
                    const c = countries.find((x) => x.name === e.target.value);
                    setSelectedCountry(c?.id);
                  }}
                  placeholder="Select Country"
                />

                <CustomSelect
                  label="State"
                  name="state"
                  value={formData.state || ''}
                  options={filteredStates}
                  onChange={(e) => {
                    handleInputChange(e);
                    const s = states.find((x) => x.name === e.target.value);
                    setSelectedState(s?.id);
                  }}
                  placeholder="Select State"
                />

                <CustomSelect
                  label="City"
                  name="city"
                  value={formData.city || ''}
                  options={filteredCities}
                  onChange={handleInputChange}
                  placeholder="Select City"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <CustomInput
                  label="Area / Locality"
                  name="area"
                  value={formData.area || ''}
                  onChange={handleInputChange}
                  placeholder="e.g. Bandra West"
                />
                <div className="sm:col-span-2">
                  <CustomInput
                    label="Full Residential Address"
                    name="location"
                    value={formData.location || ''}
                    onChange={handleInputChange}
                    placeholder="House/Flat number, Street name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <CustomInput
                  label="Pincode / ZIP"
                  name="pincode"
                  type="number"
                  value={formData.pincode || ''}
                  onChange={(e) => {
                    handleInputChange(e);
                    onPincodeChange(e.target.value);
                  }}
                  placeholder="e.g. 400050"
                />
                {(pincodeLoading || pincodeError) && (
                  <span className={`text-[11px] sm:col-span-3 -mt-3 ${pincodeError && !pincodeLoading ? 'text-rose-500' : 'text-slate-500'}`}>
                    {pincodeLoading ? 'Looking up…' : pincodeError}
                  </span>
                )}
              </div>
            </div>

            {/* Section 3: Professional & Physical Details */}
            <div>
              <h3 className="flex items-center gap-2 text-sm sm:text-base font-semibold text-slate-800 border-l-4 border-sky-500 pl-3 mb-4">
                <FiActivity className="w-4 h-4 text-sky-600" />
                Demographics & Health Details
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <CustomSelect
                  label="Education"
                  name="education"
                  value={formData.education || ''}
                  options={optionsFor('education', 'education')}
                  onChange={handleInputChange}
                  placeholder="Select Education"
                />
                <CustomSelect
                  label="Profession"
                  name="profession"
                  value={formData.profession || ''}
                  options={optionsFor('profession', 'profession')}
                  onChange={handleInputChange}
                  placeholder="Select Profession"
                />
                <CustomInput
                  label="Industry / Field"
                  name="industry"
                  value={formData.industry || ''}
                  onChange={handleInputChange}
                  placeholder="e.g. Healthcare, IT, Finance"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <CustomInput
                  label="Weight (kg)"
                  name="weight"
                  type="number"
                  value={formData.weight || ''}
                  onChange={handleInputChange}
                  placeholder="e.g. 68"
                />
                <CustomInput
                  label="Height (cm)"
                  name="height"
                  type="number"
                  value={formData.height || ''}
                  onChange={handleInputChange}
                  placeholder="e.g. 175"
                />
              </div>
            </div>

            {/* Submit Bar */}
            <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-end gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="w-full sm:w-auto px-8 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm transition shadow-sm disabled:opacity-60 cursor-pointer flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                    Saving Details...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4" />
                    Save & Submit Details
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Phase 4: Success Screen */}
        {phase === 'success' && (
          <div className="max-w-md mx-auto w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-100">
              <FiCheckCircle className="w-8 h-8" />
            </div>

            <h2 className="text-2xl font-bold text-slate-900">
              Thank You!
            </h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Your patient details have been successfully updated in the clinic records.
            </p>

            <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600 text-left space-y-1.5">
              <p>
                <strong>Patient:</strong> {formData.firstName} {formData.surname}
              </p>
              <p>
                <strong>Clinic:</strong> {info?.clinicName || 'Clinic Management'}
              </p>
              <p>
                <strong>Status:</strong> Records Synchronized
              </p>
            </div>

            <p className="mt-6 text-xs text-slate-400">
              You can now safely close this window.
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="mt-8 text-center text-xs text-slate-500">
        &copy; {new Date().getFullYear()} {info?.clinicName || 'Clinic Management'}. All rights reserved.
      </footer>
    </div>
  );
}

